/**
 * The load pipeline (architecture §4.2):
 *
 * ```
 * raw = marg.save.0 → JSON.parse → checksum → envelope migrations
 * → per SliceDef: missing → create(); v < version → migrations[v], [v+1], …; v > version → READ-ONLY (never overwrite)
 * → validate() every slice; an invalid slice alone is re-created
 * Any failure before slices → try marg.save.1, then .2 → else a fresh save + the raw string kept under marg.save.corrupt.<ts>
 * ```
 *
 * Pure apart from the injected storage: time comes from `nowWall`, seeds from `newSeed`.
 */
import { SLICE_KEYS, STORAGE_KEYS, type SaveState, type SliceDef, type SliceKey } from '../../shared';
import { parseEnvelope } from './envelope';
import { CURRENT_SCHEMA, type EnvelopeMigrations } from './migrations';
import type { KeyValueStorage } from './storage';

/** At most this many `marg.save.corrupt.*` copies are kept (architecture §4.1). */
export const MAX_CORRUPT_COPIES = 2;

export type AnySlice = SliceDef<{ v: number }>;

export interface NormalizeResult {
  state: SaveState;
  /** Slices written by a newer build (v > version): the session is read-only. */
  readOnlySlices: SliceKey[];
  /** Slices absent from the input, created fresh by their owners (normal for partial fixtures). */
  created: SliceKey[];
  /** Slices re-created because they were malformed, failed to migrate or failed validation. */
  recreated: SliceKey[];
  /** Slices migrated, with their from-versions. */
  migrated: Array<{ key: SliceKey; from: number; to: number }>;
  messages: string[];
}

export interface LoadResult extends NormalizeResult {
  /** Where the state came from. */
  source: 'save.0' | 'save.1' | 'save.2' | 'fresh';
  /** No usable save existed (first launch or every copy corrupt). */
  fresh: boolean;
  /** `marg.save.corrupt.*` keys written during this load. */
  corruptKeys: string[];
}

export interface LoadOptions {
  storage: KeyValueStorage;
  slices: readonly AnySlice[];
  nowWall: number;
  build: string;
  newSeed: () => string;
  envelopeMigrations?: EnvelopeMigrations;
}

const isObj = (x: unknown): x is Record<string, unknown> => typeof x === 'object' && x !== null && !Array.isArray(x);

/** A brand-new save: every slice from `create(seed, nowWall)`. */
export function createFreshState(slices: readonly AnySlice[], seed: string, nowWall: number, build: string): SaveState {
  const out = {} as Record<SliceKey, { v: number } & Record<string, unknown>>;
  for (const key of SLICE_KEYS) {
    const def = slices.find((s) => s.key === key);
    out[key] = def ? (def.create(seed, nowWall) as { v: number } & Record<string, unknown>) : { v: 1 };
  }
  return { schema: CURRENT_SCHEMA as 1, createdAt: nowWall, updatedAt: nowWall, lastActiveAt: nowWall, seed, build, slices: out as SaveState['slices'] };
}

/** Migrates one slice value from its v to `def.version`; throws on a missing step or a step that does not bump v. */
export function migrateSlice(def: AnySlice, value: { v: number }): { v: number } {
  let cur: { v: number } = value;
  while (cur.v < def.version) {
    const step = def.migrations[cur.v];
    if (!step) throw new Error(`${def.key}: no migration from v${cur.v}`);
    const next = step(structuredClone(cur)) as { v: number };
    if (!isObj(next) || typeof next.v !== 'number' || next.v <= cur.v) throw new Error(`${def.key}: migration from v${cur.v} did not bump v`);
    cur = next;
  }
  return cur;
}

/**
 * Turns any partial or old save-shaped object into a valid current `SaveState` (slice migrations + validation).
 * Used by the load pipeline, fixtures, `__marg.setSave` and import. Never throws for slice problems: they are
 * re-created and reported. Throws only when `raw` is not an object.
 */
export function normalizeState(raw: unknown, slices: readonly AnySlice[], nowWall: number, build: string, newSeed: () => string): NormalizeResult {
  if (!isObj(raw)) throw new Error('save: data is not an object');
  const messages: string[] = [];
  const readOnlySlices: SliceKey[] = [];
  const recreated: SliceKey[] = [];
  const created: SliceKey[] = [];
  const migrated: NormalizeResult['migrated'] = [];
  const num = (x: unknown, d: number): number => (typeof x === 'number' && Number.isFinite(x) ? x : d);
  const seed = typeof raw.seed === 'string' && raw.seed.length > 0 ? raw.seed : newSeed();
  const createdAt = num(raw.createdAt, nowWall);
  const rawSlices = isObj(raw.slices) ? raw.slices : {};
  for (const k of Object.keys(rawSlices)) {
    if (!(SLICE_KEYS as readonly string[]).includes(k)) messages.push(`dropped unknown slice "${k}"`);
  }
  const out = {} as Record<SliceKey, { v: number } & Record<string, unknown>>;
  for (const key of SLICE_KEYS) {
    const def = slices.find((s) => s.key === key);
    const value = rawSlices[key];
    if (!def) { out[key] = isObj(value) && typeof value.v === 'number' ? (value as { v: number }) : { v: 1 }; continue; }
    const fresh = (): { v: number } & Record<string, unknown> => def.create(seed, nowWall) as { v: number } & Record<string, unknown>;
    if (value === undefined) { out[key] = fresh(); created.push(key); continue; }
    if (!isObj(value) || typeof value.v !== 'number') {
      messages.push(`${key}: not a slice object, re-created`);
      out[key] = fresh();
      recreated.push(key);
      continue;
    }
    if (value.v > def.version) {
      // Written by a newer build: keep it untouched and make the session read-only (architecture §4.2).
      messages.push(`${key}: v${value.v} is newer than v${def.version} (read-only)`);
      readOnlySlices.push(key);
      out[key] = value as { v: number } & Record<string, unknown>;
      continue;
    }
    let cur: { v: number };
    try {
      const from = value.v;
      cur = migrateSlice(def, value as { v: number });
      if (from !== cur.v) migrated.push({ key, from, to: cur.v });
    } catch (e) {
      messages.push(`${key}: ${(e as Error).message}; re-created`);
      out[key] = fresh();
      recreated.push(key);
      continue;
    }
    const problems = def.validate(cur);
    if (problems.length > 0) {
      messages.push(`${key}: invalid (${problems.slice(0, 3).join('; ')}); re-created`);
      out[key] = fresh();
      recreated.push(key);
      continue;
    }
    out[key] = cur as { v: number } & Record<string, unknown>;
  }
  const state: SaveState = {
    schema: CURRENT_SCHEMA as 1,
    createdAt,
    updatedAt: num(raw.updatedAt, createdAt),
    lastActiveAt: num(raw.lastActiveAt, num(raw.updatedAt, createdAt)),
    seed,
    build: typeof raw.build === 'string' ? raw.build : build,
    slices: out as SaveState['slices'],
  };
  return { state, readOnlySlices, created, recreated, migrated, messages };
}

/** Keeps a raw unreadable save under `marg.save.corrupt.<wallMs>`, trimming to {@link MAX_CORRUPT_COPIES}. */
export function keepCorrupt(storage: KeyValueStorage, raw: string, nowWall: number): string | null {
  let key = `${STORAGE_KEYS.corruptPrefix}${nowWall}`;
  for (let i = 1; storage.getItem(key) !== null; i++) key = `${STORAGE_KEYS.corruptPrefix}${nowWall + i}`;
  try { storage.setItem(key, raw); } catch { return null; }
  const all = storage.keys().filter((k) => k.startsWith(STORAGE_KEYS.corruptPrefix))
    .sort((a, b) => Number(a.slice(STORAGE_KEYS.corruptPrefix.length)) - Number(b.slice(STORAGE_KEYS.corruptPrefix.length)));
  while (all.length > MAX_CORRUPT_COPIES) storage.removeItem(all.shift()!);
  return key;
}

/** Runs the whole load pipeline against a storage. */
export function loadSave(opts: LoadOptions): LoadResult {
  const { storage, slices, nowWall, build, newSeed } = opts;
  const corruptKeys: string[] = [];
  const messages: string[] = [];
  const sources = ['save.0', 'save.1', 'save.2'] as const;
  for (let i = 0; i < STORAGE_KEYS.save.length; i++) {
    const raw = storage.getItem(STORAGE_KEYS.save[i]);
    if (raw === null) continue;
    const parsed = parseEnvelope(raw, opts.envelopeMigrations);
    if (!parsed.ok) {
      messages.push(`${STORAGE_KEYS.save[i]}: ${parsed.error}`);
      // A newer-schema envelope is not corrupt: it must never be overwritten. Treat it as read-only fresh session.
      if (/newer than/.test(parsed.error)) {
        const fresh = createFreshState(slices, newSeed(), nowWall, build);
        return { state: fresh, readOnlySlices: [...SLICE_KEYS], created: [...SLICE_KEYS], recreated: [], migrated: [], messages, source: 'fresh', fresh: true, corruptKeys };
      }
      const kept = keepCorrupt(storage, raw, nowWall);
      if (kept) corruptKeys.push(kept);
      continue;
    }
    try {
      const norm = normalizeState(parsed.envelope.data, slices, nowWall, build, newSeed);
      return { ...norm, messages: [...messages, ...norm.messages], source: sources[i], fresh: false, corruptKeys };
    } catch (e) {
      messages.push(`${STORAGE_KEYS.save[i]}: ${(e as Error).message}`);
      const kept = keepCorrupt(storage, raw, nowWall);
      if (kept) corruptKeys.push(kept);
    }
  }
  const state = createFreshState(slices, newSeed(), nowWall, build);
  return { state, readOnlySlices: [], created: [...SLICE_KEYS], recreated: [], migrated: [], messages, source: 'fresh', fresh: true, corruptKeys };
}
